(function (root, factory) {
  const model = typeof module === 'object' && module.exports
    ? require('./dim-text-model.js')
    : root.DIMTextModel;
  const contextAuthority = typeof module === 'object' && module.exports
    ? require('./dim-context-continuation.js')
    : root.DIMContextContinuation;
  const sceneIdentity = () => typeof module === 'object' && module.exports
    ? require('./dim-scene-identity.js') : root.DIMSceneIdentity;
  const api = factory(model, contextAuthority, sceneIdentity);
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.DIMPrintableScene = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, (model, contextAuthority, sceneIdentity) => {
  'use strict';

  const IDENTITY_FIELDS = Object.freeze([
    'variantId', 'releaseId', 'profileKey', 'profileVersion', 'profileContentChecksum',
    'profileChecksum', 'outputWidth', 'outputHeight', 'initialPlacement'
  ]);
  const CONTEXT_FIELDS = Object.freeze(['storefrontId', 'bindingId', 'mode', 'purpose']);

  const clone = (value) => {
    if (Array.isArray(value)) return value.map(clone);
    if (value && typeof value === 'object') {
      const result = {};
      for (const [key, item] of Object.entries(value)) result[key] = clone(item);
      return result;
    }
    return value;
  };

  const freeze = (value) => {
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
      Object.values(value).forEach(freeze);
      Object.freeze(value);
    }
    return value;
  };

  const deepFreezeClone = (value) => freeze(clone(value));

  const requiredString = (value, field) => {
    if (typeof value !== 'string' || !value.trim() || value.length > 512) throw new Error(`Invalid ${field}.`);
    return value;
  };

  const requiredRevision = (value, field) => {
    if (!Number.isSafeInteger(value) || value < 0) throw new Error(`Invalid ${field}.`);
    return value;
  };

  const sanitise = (state) => {
    if (!state || typeof state !== 'object' || Array.isArray(state)) throw new Error('Invalid printable scene.');
    // Preserve the complete admitted authority for asynchronous operation commits.
    // Pixel reuse below deliberately uses only the stable printable context.
    const context = contextAuthority.validateContext(state.context);
    const identity = {};
    for (const field of IDENTITY_FIELDS) {
      if (!Object.hasOwn(state.identity || {}, field)) throw new Error(`Missing scene identity ${field}.`);
      identity[field] = state.identity[field];
    }
    if (!Number.isSafeInteger(identity.profileVersion) || identity.profileVersion < 1) {
      throw new Error('Invalid profile version.');
    }
    for (const field of ['outputWidth', 'outputHeight']) {
      if (!Number.isSafeInteger(identity[field]) || identity[field] < 1) throw new Error(`Invalid ${field}.`);
    }
    for (const field of ['variantId', 'releaseId', 'profileKey', 'profileContentChecksum', 'profileChecksum', 'initialPlacement']) {
      requiredString(identity[field], field);
    }
    const editorState = model.freezeEditorState(state.editorState);
    const composition = state.composition === undefined ? null : sceneIdentity().validateComposition(state.composition, {
      originalImageKey: state.originalKey, originalRevision: state.originalRevision
    });
    if (composition) sceneIdentity().validateCompositionText(composition, editorState.textLayers);
    if (Number(editorState.outputWidth) !== identity.outputWidth
      || Number(editorState.outputHeight) !== identity.outputHeight) {
      throw new Error('Scene geometry does not match the resolved output.');
    }
    for (const field of ['x', 'y', 'scale', 'rotation']) {
      if (!Number.isFinite(Number(editorState[field]))) throw new Error('Invalid image transform.');
    }
    if (Number(editorState.scale) <= 0) throw new Error('Invalid image transform.');
    if (composition && (editorState.x !== 0 || editorState.y !== 0 || editorState.scale !== 1 || editorState.rotation !== 0)) {
      throw new Error('Words-only composition cannot contain an image transform.');
    }
    return deepFreezeClone({
      runtimeScope: requiredString(state.runtimeScope, 'runtime scope'),
      recordScope: requiredString(state.recordScope, 'record scope'),
      context,
      draftRevision: requiredRevision(state.draftRevision, 'draft revision'),
      editorRevision: requiredRevision(state.editorRevision, 'editor revision'),
      productId: requiredString(state.productId, 'product ID'),
      originalKey: composition ? '' : requiredString(state.originalKey, 'original key'),
      originalRevision: requiredRevision(state.originalRevision, 'original revision'),
      ...(composition ? { composition } : {}),
      identity,
      editorState
    });
  };

  const sameContext = (left, right) => CONTEXT_FIELDS.every((field) => left.context[field] === right.context[field]);
  const sameIdentity = (left, right) => IDENTITY_FIELDS.every((field) => left.identity[field] === right.identity[field]);

  const sameSanitisedScene = (left, right) => left.runtimeScope === right.runtimeScope
    && left.recordScope === right.recordScope
    && sameContext(left, right)
    && left.productId === right.productId
    && left.originalKey === right.originalKey
    && left.originalRevision === right.originalRevision
    && JSON.stringify(left.composition) === JSON.stringify(right.composition)
    && sameIdentity(left, right)
    && model.editorStatesEqual(left.editorState, right.editorState);

  const captureOperation = (state) => sanitise(state);

  const samePrintableScene = (left, right) => {
    try {
      return sameSanitisedScene(sanitise(left), sanitise(right));
    } catch {
      return false;
    }
  };

  const canCommitOperation = (snapshot, current) => {
    try {
      const left = sanitise(snapshot);
      const right = sanitise(current);
      return left.draftRevision === right.draftRevision
        && left.editorRevision === right.editorRevision
        && contextAuthority.sameAuthorityIdentity(left.context, right.context)
        && sameSanitisedScene(left, right);
    } catch {
      return false;
    }
  };

  const printableSceneFingerprint = (state) => {
    const snapshot = sanitise(state);
    return JSON.stringify({
      runtimeScope: snapshot.runtimeScope,
      recordScope: snapshot.recordScope,
      context: Object.fromEntries(CONTEXT_FIELDS.map((field) => [field, snapshot.context[field]])),
      productId: snapshot.productId,
      originalKey: snapshot.originalKey,
      originalRevision: snapshot.originalRevision,
      ...(snapshot.composition ? { composition: snapshot.composition } : {}),
      identity: snapshot.identity,
      editorState: {
        version: snapshot.editorState.version,
        outputWidth: snapshot.editorState.outputWidth,
        outputHeight: snapshot.editorState.outputHeight,
        x: snapshot.editorState.x,
        y: snapshot.editorState.y,
        scale: snapshot.editorState.scale,
        rotation: snapshot.editorState.rotation,
        textSchemaVersion: snapshot.editorState.textSchemaVersion,
        textRendererVersion: snapshot.editorState.textRendererVersion,
        textLayers: snapshot.editorState.textLayers
      }
    });
  };

  const makePreparedReference = (state, result) => {
    if (!result || typeof result !== 'object') throw new Error('Invalid prepared result.');
    const snapshot = sanitise(state);
    requiredString(result.blobRecordId, 'prepared blob record ID');
    if (typeof result.pngFingerprint !== 'string' || !/^[a-f0-9]{64}$/i.test(result.pngFingerprint)) {
      throw new Error('Invalid PNG fingerprint.');
    }
    if (result.canRenderText !== undefined && typeof result.canRenderText !== 'boolean') {
      throw new Error('Invalid lettering preparation capability.');
    }
    if (result.canRenderText === false && snapshot.editorState.textLayers.length) {
      throw new Error('This saved lettering cannot be prepared.');
    }
    model.validateLayers(snapshot.editorState.textLayers, { forExport: true });
    if (snapshot.composition) sceneIdentity().validateCompositionText(snapshot.composition, snapshot.editorState.textLayers, { forExport: true });
    return deepFreezeClone({
      schemaVersion: 'prepared-scene@2',
      sceneFingerprint: printableSceneFingerprint(snapshot),
      snapshot,
      blobRecordId: result.blobRecordId,
      pngFingerprint: result.pngFingerprint.toLowerCase()
    });
  };

  const isPreparedReusable = (prepared, current) => {
    try {
      if (!prepared || prepared.schemaVersion !== 'prepared-scene@2') return false;
      if (!prepared.blobRecordId || !/^[a-f0-9]{64}$/i.test(prepared.pngFingerprint)) return false;
      if (prepared.sceneFingerprint !== printableSceneFingerprint(prepared.snapshot)) return false;
      model.validateLayers(prepared.snapshot.editorState.textLayers, { forExport: true });
      if (prepared.snapshot.composition) sceneIdentity().validateCompositionText(prepared.snapshot.composition,
        model.validateLayers(prepared.snapshot.editorState.textLayers, { forExport: true }), { forExport: true });
      return sameSanitisedScene(sanitise(prepared.snapshot), sanitise(current));
    } catch {
      return false;
    }
  };

  return Object.freeze({
    IDENTITY_FIELDS,
    CONTEXT_FIELDS,
    deepFreezeClone,
    captureOperation,
    samePrintableScene,
    canCommitOperation,
    printableSceneFingerprint,
    makePreparedReference,
    isPreparedReusable
  });
}));
